import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { driversAPI, apiUtils } from '@/services/api';

// État initial
const initialState = {
  // Chauffeurs
  drivers: [],
  driversLoading: false,
  driversError: null,
  driversPagination: null,
  
  // Statistiques
  driverStatistics: null,
  statisticsLoading: false,
  statisticsError: null,
  
  // Filtres et recherche
  filters: {
    search: '',
    status: '',
    assigned_vehicle: '',
    license_status: '',
    transporter_id: null,
  },
  
  // Pagination
  pagination: {
    page: 1,
    pageSize: 20,
    total: 0,
  },
  
  // Actions en cours
  actions: {
    creating: false,
    updating: false,
    deleting: false,
    assigning: false,
  },
};

export const useDriversStore = create(
  devtools(
    (set, get) => ({
      ...initialState,
      
      // ===== ACTIONS CHAUFFEURS =====
      
      // Charger les chauffeurs
      loadDrivers: async (params = {}) => {
        set({ driversLoading: true, driversError: null });
        
        try {
          const currentFilters = get().filters;
          const currentPagination = get().pagination;
          
          const queryParams = {
            ...currentFilters,
            ...params,
            page: params.page || currentPagination.page,
            page_size: params.pageSize || currentPagination.pageSize,
          };
          
          // Nettoyer les paramètres vides
          Object.keys(queryParams).forEach(key => {
            if (queryParams[key] === '' || queryParams[key] === 'all' || queryParams[key] === null || queryParams[key] === undefined) {
              delete queryParams[key];
            }
          });
          
          const response = await driversAPI.getDrivers(queryParams);
          
          set({
            drivers: response.data.results || response.data,
            driversLoading: false,
            driversPagination: response.data.pagination || null,
            pagination: {
              ...currentPagination,
              page: queryParams.page || 1,
              pageSize: queryParams.page_size || 20,
              total: response.data.count || (response.data.results ? response.data.results.length : 0),
            },
          });
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({
            driversLoading: false,
            driversError: apiError,
          });
          throw apiError;
        }
      },
      
      // Charger les chauffeurs d'un transporteur
      loadDriversByTransporter: async (transporterId, params = {}) => {
        set({ driversLoading: true, driversError: null });
        
        try {
          const response = await driversAPI.getDriversByTransporter(transporterId, params);
          
          set({
            drivers: response.data.drivers || response.data,
            driversLoading: false,
            driverStatistics: response.data.statistics || null,
          });
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({
            driversLoading: false,
            driversError: apiError,
          });
          throw apiError;
        }
      },
      
      // Charger un chauffeur par ID
      loadDriverById: async (id) => {
        try {
          const response = await driversAPI.getDriverById(id);
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          throw apiError;
        }
      },
      
      // Créer un chauffeur
      createDriver: async (driverData) => {
        set({ actions: { ...get().actions, creating: true } });
        
        try {
          // Validation côté client
          const validation = apiUtils.validateDriverData(driverData);
          if (!validation.isValid) {
            throw {
              type: 'validation',
              message: 'Données invalides',
              details: validation.errors,
            };
          }
          
          const response = await driversAPI.createDriver(driverData);
          
          // Ajouter le nouveau chauffeur à la liste
          set(state => ({
            drivers: [response.data, ...state.drivers],
            actions: { ...state.actions, creating: false },
          }));
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({ actions: { ...get().actions, creating: false } });
          throw apiError;
        }
      },
      
      // Mettre à jour un chauffeur
      updateDriver: async (id, driverData) => {
        set({ actions: { ...get().actions, updating: true } });
        
        try {
          const response = await driversAPI.updateDriver(id, driverData);
          
          // Mettre à jour le chauffeur dans la liste
          set(state => ({
            drivers: state.drivers.map(driver =>
              driver.id === id ? response.data : driver
            ),
            actions: { ...state.actions, updating: false },
          }));
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({ actions: { ...get().actions, updating: false } });
          throw apiError;
        }
      },
      
      // Supprimer un chauffeur
      deleteDriver: async (id) => {
        set({ actions: { ...get().actions, deleting: true } });
        
        try {
          await driversAPI.deleteDriver(id);
          
          // Retirer le chauffeur de la liste
          set(state => ({
            drivers: state.drivers.filter(driver => driver.id !== id),
            actions: { ...state.actions, deleting: false },
          }));
          
          return true;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({ actions: { ...get().actions, deleting: false } });
          throw apiError;
        }
      },
      
      // ===== ACTIONS ASSIGNATION VÉHICULES =====
      
      // Assigner un véhicule à un chauffeur
      assignVehicle: async (driverId, vehicleId) => {
        set({ actions: { ...get().actions, assigning: true } });
        
        try {
          const response = await driversAPI.assignVehicle(driverId, vehicleId);
          
          // Mettre à jour le chauffeur dans la liste
          set(state => ({
            drivers: state.drivers.map(driver =>
              driver.id === driverId ? response.data : driver
            ),
            actions: { ...state.actions, assigning: false },
          }));
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({ actions: { ...get().actions, assigning: false } });
          throw apiError;
        }
      },
      
      // Désassigner un véhicule d'un chauffeur
      unassignVehicle: async (driverId) => {
        set({ actions: { ...get().actions, assigning: true } });
        
        try {
          const response = await driversAPI.unassignVehicle(driverId);
          
          // Mettre à jour le chauffeur dans la liste
          set(state => ({
            drivers: state.drivers.map(driver =>
              driver.id === driverId ? response.data : driver
            ),
            actions: { ...state.actions, assigning: false },
          }));
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({ actions: { ...get().actions, assigning: false } });
          throw apiError;
        }
      },
      
      // ===== ACTIONS STATISTIQUES =====
      
      // Charger les statistiques
      loadStatistics: async (params = {}) => {
        set({ statisticsLoading: true, statisticsError: null });
        
        try {
          const response = await driversAPI.getDriverStatistics(params);
          
          set({
            driverStatistics: response.data,
            statisticsLoading: false,
          });
          
          return response.data;
        } catch (error) {
          const apiError = apiUtils.handleApiError(error);
          set({
            statisticsLoading: false,
            statisticsError: apiError,
          });
          throw apiError;
        }
      },
      
      // ===== ACTIONS FILTRES ET RECHERCHE =====
      
      // Mettre à jour les filtres
      updateFilters: (newFilters) => {
        set(state => ({
          filters: { ...state.filters, ...newFilters },
          pagination: { ...state.pagination, page: 1 }, // Reset à la page 1
        }));
      },
      
      // Réinitialiser les filtres
      resetFilters: () => {
        set({
          filters: initialState.filters,
          pagination: { ...get().pagination, page: 1 },
        });
      },
      
      // Mettre à jour la pagination
      updatePagination: (pagination) => {
        set(state => ({
          pagination: { ...state.pagination, ...pagination },
        }));
      },
      
      // ===== ACTIONS UTILITAIRES =====
      
      // Réinitialiser l'état
      reset: () => {
        set(initialState);
      },
      
      // Réinitialiser les erreurs
      clearErrors: () => {
        set({
          driversError: null,
          statisticsError: null,
        });
      },
      
      // Formater les données des chauffeurs
      getFormattedDrivers: () => {
        const drivers = get().drivers;
        return drivers.map(driver => apiUtils.formatDriverData(driver));
      },
      
      // Obtenir un chauffeur par ID depuis le store
      getDriverById: (id) => {
        const drivers = get().drivers;
        return drivers.find(driver => driver.id === id);
      },
      
      // Vérifier si un chauffeur est en cours de traitement
      isDriverProcessing: (id) => {
        const actions = get().actions;
        return actions.creating || actions.updating || actions.deleting || actions.assigning;
      },
      
      // Obtenir les chauffeurs assignés à un véhicule
      getDriversByVehicle: (vehicleId) => {
        const drivers = get().drivers;
        return drivers.filter(driver => driver.assigned_vehicle === vehicleId);
      },
      
      // Obtenir les chauffeurs non assignés
      getUnassignedDrivers: () => {
        const drivers = get().drivers;
        return drivers.filter(driver => !driver.assigned_vehicle);
      },
    }),
    {
      name: 'drivers-store',
    }
  )
);
